export type MenuCategoryKey =
  | 'DASHBOARD'
  | 'PATIENT'
  | 'STAFF'
  | 'PRO_MGMT'
  | 'METADATA'
  | 'CONTENT_LIB'
  | 'CUSTOMER_SUPPORT'
  | 'PUBLICATION'
  | 'SYSTEM_MGMT';

export const MENU_CATEGORY_KEYS: readonly MenuCategoryKey[] = [
  'DASHBOARD',
  'PATIENT',
  'STAFF',
  'PRO_MGMT',
  'METADATA',
  'CONTENT_LIB',
  'CUSTOMER_SUPPORT',
  'PUBLICATION',
  'SYSTEM_MGMT',
] as const;

export type PermissionValue = 'NO_PERMISSION' | 'READ' | 'WRITE';

export type PermissionMap = Record<MenuCategoryKey, PermissionValue>;

export interface RoleAuthor {
  id: number | null;
  name: string;
}

export interface Role {
  id: number;
  name: string;
  permissionMap: PermissionMap;
  isReserved: boolean;
  isSeed: boolean;
  adminCount: number;
  createdAt: string;
  updatedAt: string;
  createdBy: RoleAuthor;
}

export interface CreateRoleInput {
  name: string;
  permissionMap: PermissionMap;
}

export interface UpdateRoleInput {
  name?: string;
  permissionMap?: PermissionMap;
}

export interface DeleteRoleResult {
  affectedAdminIds: number[];
  affectedCount: number;
}

export type RoleApiErrorCode =
  | 'ROLE_NAME_INVALID'
  | 'ROLE_NAME_DUPLICATE'
  | 'RESERVED_ROLE'
  | 'SEED_ROLE_NOT_DELETABLE'
  | 'ROLE_IN_USE_BY_SELF'
  | 'INSUFFICIENT_PERMISSION'
  | 'NOT_FOUND'
  // SHCT-631 — the server's 5xx envelope really does send INTERNAL_ERROR, and `mapError` casts the
  // wire code straight into this union. Leaving it out did not stop it arriving, it only hid it
  // from the exhaustiveness check in `getRoleErrorMessage`.
  | 'INTERNAL_ERROR'
  | 'NETWORK'
  | 'UNKNOWN';

export class RoleApiError extends Error {
  readonly code: RoleApiErrorCode;
  readonly fields?: Record<string, string>;

  constructor(code: RoleApiErrorCode, message: string, fields?: Record<string, string>) {
    super(message);
    this.name = 'RoleApiError';
    this.code = code;
    this.fields = fields;
    Object.setPrototypeOf(this, RoleApiError.prototype);
  }
}

export interface RoleApi {
  listRoles(): Promise<Role[]>;
  getRole(id: number): Promise<Role>;
  createRole(input: CreateRoleInput): Promise<Role>;
  updateRole(id: number, input: UpdateRoleInput): Promise<Role>;
  deleteRole(id: number): Promise<DeleteRoleResult>;
}
